<template>
  <div class="minute-editor">
    <div class="minute-editor__header">
      <h3 class="minute-editor__title">{{ title }}</h3>

      <div class="minute-editor__purpose-actions">
        <div
          v-if="selectedPurposes.length"
          class="minute-editor__purpose-section"
        >
          <icon-view
            icon="fas fa-file fa-lg text-orange"
            icon-background="white"
            title="Propósito(s) de la reunión"
          />
          <div class="minute-editor__chips">
            <span
              v-for="purpose in selectedPurposes"
              :key="getPurposeKey(purpose)"
              class="minute-editor__chip"
            >
              {{ getPurposeText(purpose) }}
            </span>
          </div>
        </div>

        <action-button
          class="minute-editor__preview-button"
          i="fas fa-eye"
          text="Previsualizar"
          variant="outline-success"
          @click="$emit('preview')"
        />
      </div>
    </div>

    <warning-box
      class="minute-editor__preview-notice ml-0"
      title="La tabla de Participantes y Firma y los Propósito(s) de la reunión que se muestran en la vista previa del documento a continuación son solo de referencia y no se pueden editar aquí. Estos datos se completan durante el proceso de firma."
    />

    <div v-if="readonly" class="minute-editor__canvas">
      <div
        class="minute-editor__readonly-content"
        v-html="localContent || '<p></p>'"
      />
    </div>
    <div v-else class="minute-editor__canvas">
      <div class="minute-editor__editor-shell">
        <enhanced-editor
          v-model="localContent"
          id="compu-minute-editor"
          closing-template-id="firmas-y-cierre"
          header-template-id="header-informativo"
          @wrapper-html-changed="handleWrapperHtmlChanged"
        />
      </div>
    </div>

    <!-- Indicador flotante de auto-guardado -->
    <transition name="slide-fade">
      <div v-if="autoSaveStatus" class="minute-editor__autosave-indicator">
        <i
          :class="[
            autoSaveStatus === 'saving' ? 'fas fa-sync-alt fa-spin' : 'fas fa-check-circle',
            autoSaveStatus === 'saving' ? 'text-muted' : 'text-success'
          ]"
        />
        <span class="minute-editor__autosave-text">
          {{ autoSaveStatus === 'saving' ? 'Guardando...' : 'Guardado' }}
        </span>
      </div>
    </transition>
  </div>
</template>

<script>
import IconView from 'icon-view';
import debounce from 'lodash/debounce';
import ActionButton from 'ActionButton';
import alertMixin from '@/mixin/alert.mixin';
import EnhancedEditor from '@/components/common/EnhancedEditor.vue';
// import { WarningBox } from '@/components/pei/tabs/components/tab8';
import WarningBox from '@/components/pei/service/components/ServiceWarningBox';

const TEMPLATE_EDITED = "Párrafo editado. Para restaurar el original, remueva y agregue nuevamente el propósito.";

export default {
  name: 'MinuteEditorTab',
  mixins: [alertMixin],
  components: {
    ActionButton,
    EnhancedEditor,
    IconView,
    WarningBox,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    generalInformation: {
      type: Object,
      default: () => ({}),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localContent: '',
      autoSaveStatus: null, // 'saving' | 'saved' | null
      autoSaveTimer: null,
    };
  },
  created() {
    this.debouncedAutoSave = debounce(() => {
      if (!this.readonly && this.localContent) {
        this.showSavingIndicator();
        this.$emit('auto-save');
      }
    }, 1500);
    this.debouncedWrapperHtmlChanged = debounce(() => {
      if (!this.readonly && this.localContent) {
        
        this.ShowToast(
          'Alerta',
          `${TEMPLATE_EDITED}`,
          'warning'
        );
        
      }
    }, 2000);
  },
  computed: {
    selectedStudent() {
      return this.generalInformation?.students?.[0] ?? {};
    },
    selectedPurposes() {
      return this.generalInformation?.purposes ?? [];
    },
    studentSie() {
      return (
        this.selectedStudent.studentSieId ??
        this.selectedStudent.studentId ??
        '-'
      );
    },
    studentName() {
      return (
        this.selectedStudent.fullName ??
        this.selectedStudent.name ??
        this.selectedStudent.studentName ??
        '-'
      );
    },
    title() {
      return `Minuta de COMPU - ${this.studentSie} - ${this.studentName}`;
    },
  },
  watch: {
    value(value) {
      this.localContent = value;
    },
    localContent(value) {
      this.$emit('input', value);
      this.$emit('change', value);
      if (this.debouncedAutoSave) {
        this.debouncedAutoSave();
      }
    },
  },
  beforeDestroy() {
    if (this.debouncedAutoSave) {
      this.debouncedAutoSave.cancel();
    }
    if (this.debouncedWrapperHtmlChanged) {
      this.debouncedWrapperHtmlChanged.cancel();
    }
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
  },
  methods: {
    getPurposeKey(purpose) {
      return purpose.id ?? purpose.name;
    },
    getPurposeText(purpose) {
      if (purpose.otherDescription) {
        return `${purpose.name}: ${purpose.otherDescription}`;
      }

      return purpose.name;
    },
    showSavingIndicator() {
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer);
      }
      this.autoSaveStatus = 'saving';
    },
    showSavedIndicator() {
      this.autoSaveStatus = 'saved';
      this.autoSaveTimer = setTimeout(() => {
        this.autoSaveStatus = null;
      }, 3000); // Ocultar después de 3 segundos
    },
    handleWrapperHtmlChanged(templateId) {
      if (this.debouncedWrapperHtmlChanged) {
        this.debouncedWrapperHtmlChanged(templateId);
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.minute-editor {
  padding-top: 1.25rem;

  &__header {
    margin-bottom: 1rem;
  }

  &__preview-notice {
    margin-bottom: 1rem;
  }

  &__title {
    margin-bottom: 0.75rem;
    color: #11291b;
    font-size: 20px;
    font-weight: 700;
  }

  &__purpose-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__purpose-actions {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  &__preview-button {
    flex: 0 0 auto;
    margin-top: 2.25rem;
  }

  &__autosave-indicator {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    font-size: 14px;
    color: #6c757d;
    white-space: nowrap;
    z-index: 1000;
    border: 1px solid #e0e0e0;

    i {
      font-size: 16px;
    }
  }

  &__autosave-text {
    font-weight: 500;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    min-height: 34px;
    padding: 0.35rem 0.85rem;
    border: 1px solid #b5c3bc;
    border-radius: 18px;
    background: #ffffff;
    color: #11291b;
    font-size: 16px;
    font-weight: 700;
  }

  &__canvas {
    display: flex;
    justify-content: center;
    min-height: 520px;
    padding: 1.25rem 1.5rem 2rem;
    background: #f4f6f3;
  }

  &__editor-shell {
    width: 100%;
    max-width: 980px;
  }

  &__readonly-content {
    width: 100%;
    max-width: 980px;
    min-height: 620px;
    padding: 2rem 3rem;
    background: #ffffff;
    color: #11291b;
    font-size: 14px;
    pointer-events: none;
    user-select: text;
  }
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}
</style>
